import React from 'react';

/**
 * Watch one of ROCCO's live sections.
 *
 * Fetches straight away, then every `everyMs` while the tab is visible, so a
 * lead arriving or a call ending shows up without anyone reloading. Keeps the
 * last good data through a failed poll: a blip should not blank the screen.
 *
 * `reconnecting` is true while the server cannot be reached but earlier data
 * is still on screen, so a page can say it is out of date rather than look
 * complete. It clears on the next good fetch.
 *
 * With `watch`, the server also says the moment something changed, and the
 * section fetches then. The timer stays underneath, slowed to `backupMs`
 * while the stream is open, and back to `everyMs` whenever it is not — so a
 * dropped stream costs a few seconds, never the page.
 */
export function useLive<T>(
  fetcher: () => Promise<T>,
  everyMs = 5000,
  options: {
    watch?: (onChange: () => void, signal: AbortSignal, onOpen?: () => void) => Promise<void>;
    backupMs?: number;
  } = {},
) {
  const [data, setData] = React.useState<T | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [streaming, setStreaming] = React.useState(false);
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;
  const { watch, backupMs = 30_000 } = options;

  const load = React.useCallback(async () => {
    try {
      setData(await fetcherRef.current());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load');
    }
  }, []);

  React.useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, streaming ? backupMs : everyMs);
    return () => window.clearInterval(id);
  }, [load, everyMs, backupMs, streaming]);

  React.useEffect(() => {
    if (!watch) return undefined;
    const controller = new AbortController();
    const backoff = { ms: 1000 };
    (async () => {
      while (!controller.signal.aborted) {
        try {
          await watch(load, controller.signal, () => { setStreaming(true); backoff.ms = 1000; load(); });
        } catch {
          // Refused, dropped or offline: the timer carries on meanwhile.
        }
        setStreaming(false);
        if (controller.signal.aborted) return;
        await new Promise((resolve) => window.setTimeout(resolve, backoff.ms));
        backoff.ms = Math.min(backoff.ms * 2, 30_000);
      }
    })();
    return () => controller.abort();
  }, [watch, load]);

  return {
    data,
    error,
    loading: data === null && error === null,
    reload: load,
    // A dropped stream on its own is not "reconnecting": the timer carries on.
    // It is when a fetch has failed and the screen is showing older data.
    streaming: streaming && !(error && data),
    reconnecting: Boolean(error && data),
  };
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const timeOf = (at: Date) =>
  at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();

/** "1:37 pm" today; "Sat 26 Sep, 3:00 pm" on any other day. In the viewer's time. */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (sameDay(at, new Date())) return timeOf(at);
  const day = at.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day}, ${timeOf(at)}`;
}

/** "Sat 26 Sep, 3:00 pm", always with the day: for appointments, where "0:00" alone reads as no date. */
export function onDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  return `${at.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}, ${timeOf(at)}`;
}

/** "EDT", "PST"; "IST" for India, which the browser would call "GMT+5:30". */
function zoneAbbr(at: Date, zone: string): string {
  if (zone === 'Asia/Kolkata') return 'IST';
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at).find((p) => p.type === 'timeZoneName');
  return part?.value ?? zone;
}

/** "Tue 29 Sep, 5:00 pm EDT": a moment as it reads on the clock in `zone`, named. */
export function inZone(iso: string | null | undefined, zone: string | null | undefined): string {
  if (!iso) return '—';
  if (!zone) return onDay(iso);
  const at = new Date(iso);
  const day = at.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', timeZone: zone });
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: zone }).toLowerCase();
  return `${day}, ${time} ${zoneAbbr(at, zone)}`;
}

/** Whether a clock in `zone` reads the same as the viewer's own at that moment. */
export function isViewersClock(iso: string | null | undefined, zone: string | null | undefined): boolean {
  if (!iso || !zone) return true;
  const at = new Date(iso);
  const read = (tz?: string) => at.toLocaleString('en-US', { timeZone: tz, dateStyle: 'short', timeStyle: 'short' });
  return read(zone) === read(undefined);
}

/** "just now", "12m ago", "3h ago", "in 5m", "in 23h", "in 2d". */
export function fromNow(iso: string | null | undefined): string {
  if (!iso) return '';
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  const size = Math.abs(minutes);
  if (size < 1) return 'just now';
  const text = size < 60 ? `${size}m` : size < 48 * 60 ? `${Math.round(size / 60)}h` : `${Math.round(size / 1440)}d`;
  return minutes < 0 ? `${text} ago` : `in ${text}`;
}

/** "56s", "2m 05s". */
export function lengthOf(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
}

/**
 * Whether a row matches what was typed into a list's search: every word
 * must appear somewhere in the row's fields, in any order, any case.
 * "singh ava" finds V. Singh's call with Ava.
 */
export function matches(query: string, fields: (string | number | null | undefined)[]): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = fields.filter((field) => field !== null && field !== undefined).join(' ').toLowerCase();
  return words.every((word) => text.includes(word));
}
